import React, { useState, useEffect } from 'react';
import { getHrisSlipGaji, deleteHrisSlipGaji, generateHrisSlipGaji, generateHrisSlipGajiBulk, publishHrisSlipGaji, publishBulkHrisSlipGaji, getKaryawan, getHrisGaji, getBagian } from '../../api';
import { Plus, Trash2, FileText, Search, Loader, CheckCircle, DollarSign, Clock, Printer, ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import logoPerusahaan from '../../utils/logoPerusahaan';

// ============ Helper periode (Bulanan = YYYY-MM, Mingguan = YYYY-MM-DD hari Senin) ============
const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseISODate = (s) => {
  const [y, m, d] = String(s || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const addDays = (s, n) => {
  const d = parseISODate(s);
  if (!d) return s;
  d.setDate(d.getDate() + n);
  return toISODate(d);
};
// Senin dari minggu yang memuat tanggal tsb — dipakai supaya periode Mingguan
// selalu konsisten 1 tanggal per minggu (tidak terpecah karena beda input).
const mondayOf = (s) => {
  const d = parseISODate(s) || new Date();
  const day = d.getDay() || 7; // Senin = 1 ... Minggu = 7
  d.setDate(d.getDate() - (day - 1));
  return toISODate(d);
};
const thisWeekMonday = () => mondayOf(toISODate(new Date()));

const fmtTanggal = (s) => {
  const d = parseISODate(s);
  return d ? d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : (s || '-');
};
const fmtTanggalSingkat = (s) => {
  const d = parseISODate(s);
  return d ? d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short' }) : (s || '-');
};
// Label periode yang enak dibaca: Mingguan -> "07 Sep – 13 Sep 2026", Bulanan -> "September 2026"
const fmtPeriode = (d) => {
  const p = String((d && d.periode) || '-');
  if (((d && d.jenis_gaji) || 'Bulanan') === 'Mingguan') {
    if (!parseISODate(p)) return p;
    return `${fmtTanggalSingkat(p)} – ${fmtTanggal(addDays(p, 6))}`;
  }
  const [y, m] = p.split('-').map(Number);
  if (!y || !m) return p;
  return new Date(y, m - 1, 1).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
};
const labelRentangMinggu = (monday) => monday ? `${fmtTanggalSingkat(monday)} – ${fmtTanggal(addDays(monday, 6))}` : '-';
const btnPeriodeStyle = {
  padding: '5px 10px', borderRadius: 6, border: '1px solid var(--gray-200)',
  background: '#fff', color: 'var(--gray-500)', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export default function HrisSlipGaji() {
  const [data, setData] = useState([]);
  const [karyawan, setKaryawan] = useState([]);
  const [karyawanMap, setKaryawanMap] = useState({});
  const [gajiMap, setGajiMap] = useState({});
  const [bagianList, setBagianList] = useState([]);
  const [filterBagian, setFilterBagian] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterJenis, setFilterJenis] = useState('');
  const [filterPeriode, setFilterPeriode] = useState(thisMonth());
  const [showGenerate, setShowGenerate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showPublish, setShowPublish] = useState(null); // slip yang akan diterbitkan
  const [publishMetode, setPublishMetode] = useState('Hutang');
  const [showPublishBulk, setShowPublishBulk] = useState(false); // terbitkan massal 1 periode
  const [publishBulkMetode, setPublishBulkMetode] = useState('Hutang');
  const [genForm, setGenForm] = useState({ karyawan_id: '', periode: thisMonth() });
  const [genLoading, setGenLoading] = useState(false);

  const isWeeklyFilter = filterJenis === 'Mingguan';

  const fetchData = () => {
    setLoading(true);
    // Mingguan: cocokkan tanggal Senin persis. Bulanan/Semua: cocokkan awalan bulan
    // (periode_bulan) supaya slip Mingguan di bulan itu juga ikut tampil.
    const params = { jenis: filterJenis || undefined };
    if (filterPeriode) {
      if (isWeeklyFilter) params.periode = filterPeriode;
      else params.periode_bulan = filterPeriode;
    }
    Promise.all([getHrisSlipGaji(params), getKaryawan(), getHrisGaji(), getBagian()])
      .then(([dRes, kRes, gRes, bRes]) => {
        setData(dRes.data);
        setKaryawan(kRes.data);
        const map = {};
        (gRes.data || []).forEach(g => { map[g.karyawan_id] = g.jenis_gaji || 'Bulanan'; });
        setGajiMap(map);
        const kmap = {};
        (kRes.data || []).forEach(k => { kmap[k.id] = k; });
        setKaryawanMap(kmap);
        setBagianList(bRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [filterPeriode, filterJenis]);

  const handleGenerate = async () => {
    if (!genForm.karyawan_id || !genForm.periode) { alert('Lengkapi data'); return; }
    setGenLoading(true);
    try {
      await generateHrisSlipGaji(genForm);
      setShowGenerate(false);
      fetchData();
      alert('Slip gaji berhasil digenerate');
    } catch (e) { alert(e.response?.data?.error || 'Gagal generate'); }
    finally { setGenLoading(false); }
  };

  const handleGenerateBulk = async () => {
    if (!showBulk) return;
    setGenLoading(true);
    try {
      const res = await generateHrisSlipGajiBulk({ periode: showBulk, jenis: filterJenis || undefined, bagian_id: filterBagian ? Number(filterBagian) : 0 });
      setShowBulk(false);
      fetchData();
      alert(res.data?.message || 'Slip gaji berhasil digenerate');
    } catch (e) { alert('Gagal generate bulk'); }
    finally { setGenLoading(false); }
  };

  // Generate Minggu Ini — massal langsung dari toolbar (tanpa modal form).
  // Selalu memakai MINGGU BERJALAN (Senin minggu ini), bukan periode yang sedang difilter,
  // supaya 1 klik = slip mingguan minggu ini untuk semua karyawan aktif.
  const handleGenerateThisWeek = async () => {
    const periode = thisWeekMonday();
    const bagianNama = filterBagian
      ? (bagianList.find(b => String(b.id) === String(filterBagian))?.name || '')
      : '';
    const ok = window.confirm(
      `Generate slip gaji MINGGUAN massal untuk semua karyawan aktif${bagianNama ? ` di bagian ${bagianNama}` : ''}\n` +
      `Periode: ${labelRentangMinggu(periode)}\n\n` +
      'Slip yang sudah ada di minggu ini akan diperbarui (tidak dobel).'
    );
    if (!ok) return;
    setGenLoading(true);
    try {
      const res = await generateHrisSlipGajiBulk({
        periode, jenis: 'Mingguan',
        bagian_id: filterBagian ? Number(filterBagian) : 0,
      });
      // Tampilkan hasilnya: pindah filter ke Mingguan + minggu berjalan
      setFilterJenis('Mingguan');
      setFilterPeriode(periode);
      fetchData();
      alert(res.data?.message || 'Slip gaji mingguan berhasil digenerate');
    } catch (e) { alert(e.response?.data?.error || 'Gagal generate mingguan massal'); }
    finally { setGenLoading(false); }
  };

  const openPublish = (d) => {
    setPublishMetode(d.metode_pembayaran || 'Hutang');
    setShowPublish(d);
  };

  const handlePublish = async () => {
    if (!showPublish) return;
    try {
      await publishHrisSlipGaji(showPublish.id, { metode_pembayaran: publishMetode });
      setShowPublish(null);
      fetchData();
      alert('Slip gaji diterbitkan, jurnal akuntansi otomatis dibuat');
    } catch (e) { alert(e.response?.data?.error || 'Gagal menerbitkan'); }
  };

  // Terbitkan massal: semua slip Draft pada periode + jenis (opsional 1 bagian) sekaligus,
  // masing-masing dibuatkan jurnal akuntansi otomatis oleh backend (idempotent).
  const handlePublishBulk = async () => {
    const periode = isWeeklyFilter ? mondayOf(filterPeriode) : filterPeriode;
    try {
      setGenLoading(true);
      const res = await publishBulkHrisSlipGaji({
        periode, jenis: filterJenis,
        bagian_id: filterBagian ? Number(filterBagian) : 0,
        metode_pembayaran: publishBulkMetode,
      });
      setShowPublishBulk(false);
      fetchData();
      alert(res.data?.message || 'Slip gaji berhasil diterbitkan');
    } catch (e) { alert(e.response?.data?.error || 'Gagal menerbitkan massal'); }
    finally { setGenLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus slip gaji ini?')) return;
    try { await deleteHrisSlipGaji(id); fetchData(); } catch (e) { alert('Gagal menghapus'); }
  };

  // ============ CETAK PDF SLIP GAJI (format nota/tagihan) ============
  const generateSlipPDF = (d) => {
    if (!d) return;
    try {
      const { jsPDF } = require('jspdf');
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentW = pageW - 2 * margin;
      const primary = [79, 110, 247];
      const purple = [137, 101, 224];
      const dark = [23, 43, 77];
      const gray = [136, 152, 170];
      const lightBg = [248, 249, 252];
      const headerBg = [13, 29, 54];
      const teal = [57, 143, 153];
      const green = [45, 173, 129];
      const red = [245, 54, 92];
      const mingguan = (d.jenis_gaji || 'Bulanan') === 'Mingguan';

      // ===== 1. HEADER: logo + judul + garis aksen (mirip cetak tagihan) =====
      try { doc.addImage(logoPerusahaan, 'PNG', margin, 3, 40, 22); } catch (e) { /* ignore */ }
      doc.setTextColor(...headerBg);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('SLIP GAJI', pageW - margin, 18, { align: 'right' });
      doc.setDrawColor(...teal);
      doc.setLineWidth(1);
      doc.line(margin, 20, pageW - margin, 20);
      doc.setLineWidth(0.3);
      doc.setDrawColor(180, 180, 180);

      let y = 26;

      // Profil PT (kiri)
      doc.setFontSize(6);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...gray);
      const addrLines = [
        'PT. SYNEXA GLOBAL TEKNO',
        'Graha Raya: Ruko Silktown Avenue Blok RK/5A-05',
        'Jl. Boulevard Silk Town, Kel. Pondok Jagung Timur',
        'Kec. Serpong Utara, Kota Tangerang Selatan 15320',
        'Jakarta: Office 8 SCBD Lt.17, Jl. Senopati',
        'www.synexa.co.id | info@synexa.co.id',
      ];
      addrLines.forEach((line, i) => doc.text(line, margin, y + i * 3.5));

      // Data dokumen (kanan)
      const col2X = margin + (contentW / 2) + 6;
      const invFields = [
        ['NO. SLIP', String(d.id || '-')],
        [mingguan ? 'MINGGU' : 'PERIODE', fmtPeriode(d)],
        ['JENIS', (d.jenis_gaji || 'Bulanan').toUpperCase()],
        ['STATUS', d.status || '-'],
      ];
      const rowH = 4.5;
      doc.setFontSize(7);
      invFields.forEach((f, i) => {
        const ry = y + i * rowH;
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...gray);
        doc.text(f[0] + ':', col2X, ry);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...dark);
        doc.text(f[1], col2X + 20, ry);
      });
      y += Math.max(addrLines.length * 3.5 + 2, invFields.length * rowH + 2) - 3;

      // ===== 2. DATA KARYAWAN =====
      const sectionTitle = (title, yPos, color) => {
        doc.setFillColor(...(color || primary));
        doc.rect(margin, yPos, 3, 8, 'F');
        doc.setTextColor(...dark);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(title, margin + 7, yPos + 6);
        return yPos + 14;
      };
      const drawField = (label2, value, x, yPos) => {
        doc.setTextColor(...gray);
        doc.setFontSize(7);
        doc.text(label2, x, yPos);
        doc.setTextColor(...dark);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(value || '-', x, yPos + 4);
        doc.setFont('helvetica', 'normal');
        return yPos + 11;
      };

      const kr = karyawanMap[d.karyawan_id] || {};
      y = sectionTitle('DATA KARYAWAN', y + 6) - 2;
      const leftX = margin;
      const rightX = margin + contentW / 2 + 5;
      let ly = y;
      ly = drawField('Nama Karyawan', d.nama || kr.nama || '-', leftX, ly);
      ly = drawField('NIK', kr.nik || '-', leftX, ly);
      let ry = y;
      ry = drawField('Jabatan', kr.jabatan || '-', rightX, ry);
      ry = drawField('Bagian', kr.bagian_name || '-', rightX, ry);
      y = Math.max(ly, ry) + 4;

      // ===== 3. RINCIAN GAJI (Penerimaan & Potongan) =====
      y = sectionTitle('RINCIAN GAJI', y, mingguan ? purple : primary) + 4;

      const penerimaanRows = [];
      const potonganRows = [];
      if (mingguan) {
        penerimaanRows.push(['Tunjangan Transport BBM', d.tunjangan_transport_bbm]);
        penerimaanRows.push(['Tunjangan Sewa Kendaraan', d.tunjangan_sewa_kendaraan]);
      } else {
        penerimaanRows.push(['Gaji Pokok', d.gaji_pokok]);
        penerimaanRows.push(['Tunjangan Jabatan', d.tunjangan_jabatan]);
        penerimaanRows.push(['Insentif Keahlian (AC)', d.insentif_keahlian_ac]);
        penerimaanRows.push(['Insentif Project', d.insentif_project]);
        potonganRows.push(['Potongan Alpa', d.potongan_alpa]);
        potonganRows.push(['Potongan Kasbon', d.potongan_kasbon]);
      }
      if (Number(d.lembur) > 0) penerimaanRows.push(['Lembur', d.lembur]);
      if (Number(d.bonus) > 0) penerimaanRows.push(['Bonus', d.bonus]);
      if (Number(d.bpjs_kesehatan) > 0) potonganRows.push(['BPJS Kesehatan', d.bpjs_kesehatan]);
      if (Number(d.bpjs_ketenagakerjaan) > 0) potonganRows.push(['BPJS Ketenagakerjaan', d.bpjs_ketenagakerjaan]);
      if (Number(d.potongan_lain) > 0) potonganRows.push(['Potongan Lain', d.potongan_lain]);
      if (potonganRows.length === 0) potonganRows.push(['Tidak ada potongan', 0]);
      if (penerimaanRows.length === 0) penerimaanRows.push(['Tidak ada penerimaan', 0]);

      const drawCompTable = (title, rows, x, yPos, width, color, total) => {
        const headerH = 8;
        const rowH2 = 6.5;
        doc.setFillColor(...color);
        doc.roundedRect(x, yPos, width, headerH, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(title, x + 4, yPos + 5.5);
        doc.setFont('helvetica', 'normal');
        let yy = yPos + headerH;
        rows.forEach(r => {
          doc.setFillColor(...lightBg);
          doc.rect(x, yy, width, rowH2, 'F');
          doc.setDrawColor(225, 225, 225);
          doc.setLineWidth(0.2);
          doc.rect(x, yy, width, rowH2);
          doc.setTextColor(...dark);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.5);
          doc.text(r[0], x + 4, yy + 4.5);
          doc.setFont('helvetica', 'bold');
          doc.text(fmtRupiah(r[1]), x + width - 4, yy + 4.5, { align: 'right' });
          yy += rowH2;
        });
        if (total !== undefined && total !== null) {
          doc.setFillColor(...color);
          doc.rect(x, yy, width, rowH2, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.text('Total', x + 4, yy + 4.5);
          doc.text(fmtRupiah(total), x + width - 4, yy + 4.5, { align: 'right' });
          yy += rowH2;
        }
        return yy;
      };

      // Mingguan: konsepnya hanya komponen mingguan (Transport BBM + Sewa Kendaraan)
      // dan Total — tidak ada tabel Potongan. Bulanan tetap 2 tabel.
      const halfW = (contentW - 8) / 2;
      if (mingguan) {
        y = drawCompTable('KOMPONEN MINGGUAN', penerimaanRows, margin, y, contentW, purple, d.total_penerimaan) + 10;
      } else {
        const yPenerimaan = drawCompTable('PENERIMAAN', penerimaanRows, margin, y, halfW, green, d.total_penerimaan);
        const yPotongan = drawCompTable('POTONGAN', potonganRows, margin + halfW + 8, y, halfW, red, d.total_potongan);
        y = Math.max(yPenerimaan, yPotongan) + 10;
      }

      // ===== 4. TAKE HOME PAY =====
      doc.setFillColor(...(mingguan ? purple : primary));
      doc.roundedRect(margin, y, contentW, 14, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(mingguan ? 'TOTAL DITERIMA' : 'TAKE HOME PAY', margin + 12, y + 6);
      doc.text(fmtRupiah(d.thp), pageW - margin - 12, y + 6, { align: 'right' });
      y += 24;

      if (d.keterangan) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...gray);
        const noteLines = doc.splitTextToSize(`Catatan: ${d.keterangan}`, contentW);
        doc.text(noteLines, margin, y);
        y += noteLines.length * 3.5 + 4;
      }

      // ===== 5. TANDA TANGAN (3 kolom) =====
      const footerY = pageH - 35;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, footerY, pageW - margin, footerY);
      const now = new Date();
      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text(`Dicetak pada: ${now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin, footerY + 5);
      doc.setFont('helvetica', 'normal');

      const sigW = (contentW - 24) / 3;
      const sigLabels = ['Dibuat oleh,', 'Diterima oleh,', 'Mengetahui,'];
      const sigSubs = ['Bagian Payroll', d.nama || 'Karyawan', 'HRD / Manajer'];
      sigLabels.forEach((label, i) => {
        const sx = margin + i * (sigW + 12);
        doc.setFontSize(8);
        doc.setTextColor(...gray);
        doc.text(label, sx, footerY - 22);
        doc.text('( ........................... )', sx, footerY - 12);
        doc.setFontSize(7);
        doc.setTextColor(...dark);
        doc.text(sigSubs[i], sx, footerY - 6);
      });

      doc.save(`Slip_Gaji_${(d.nama || d.id).replace(/\s+/g, '_')}_${(d.periode || '').slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('Gagal generate PDF slip gaji', err);
      alert('Gagal mencetak slip gaji');
    }
  };

  const fmtRupiah = (v) => `Rp ${Number(v || 0).toLocaleString('id-ID')}`;

  const filtered = data.filter(d => {
    if (search && !d.nama?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterBagian) {
      const kr = karyawanMap[d.karyawan_id] || {};
      if (Number(kr.bagian_id) !== Number(filterBagian)) return false;
    }
    return true;
  });

  // Kartu ringkasan mengikuti hasil filter (pencarian & bagian), sama seperti counter di toolbar.
  const totalData = filtered.length;
  const totalBulanan = filtered.filter(d => (d.jenis_gaji || 'Bulanan') === 'Bulanan').length;
  const totalMingguan = filtered.filter(d => (d.jenis_gaji || 'Bulanan') === 'Mingguan').length;
  const totalDiterbitkan = filtered.filter(d => d.status === 'Diterbitkan').length;
  const totalTHP = filtered.reduce((sum, d) => sum + Number(d.thp || 0), 0);
  const totalSemua = data.length;

  // Slip yang akan ikut terbit massal = yang masih Draft di periode+bagian yang aktif
  const dataBagian = filterBagian
    ? data.filter(d => Number((karyawanMap[d.karyawan_id] || {}).bagian_id) === Number(filterBagian))
    : data;
  const draftPeriode = dataBagian.filter(d => d.status === 'Draft');
  const totalTHPDraft = draftPeriode.reduce((sum, d) => sum + Number(d.thp || 0), 0);

  const openGenerate = () => {
    setGenForm({ karyawan_id: '', periode: isWeeklyFilter ? thisWeekMonday() : thisMonth() });
    setShowGenerate(true);
  };

  const onSelectKaryawan = (id) => {
    const jenis = gajiMap[id] || 'Bulanan';
    setGenForm({ karyawan_id: id, periode: jenis === 'Mingguan' ? thisWeekMonday() : thisMonth() });
  };

  const genIsWeekly = gajiMap[genForm.karyawan_id] === 'Mingguan';

  if (loading) return <div className="page-loading"><div className="spinner-lg" /></div>;

  return (
    <div className="page-container">
      <div className="page-header" style={{ borderBottom: '2px solid var(--gray-100)', paddingBottom: 16, marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--gray-600)' }}>
          <FileText size={22} style={{ marginRight: 8, verticalAlign: 'middle', color: 'var(--cyan-500)' }} />
          Data Slip Gaji (Payroll)
        </h2>
        <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', margin: 0 }}>Penggajian Bulanan & Mingguan</p>
      </div>

      {/* Stat Cards */}
      {filtered.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 16, marginBottom: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid var(--info)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Total Slip</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--gray-600)', lineHeight: 1.2 }}>{totalData}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid var(--cyan-500)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Bulanan</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--cyan-700)', lineHeight: 1.2 }}>{totalBulanan}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #8965e0' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Mingguan</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#5b3fc4', lineHeight: 1.2 }}>{totalMingguan}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #2dce89' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Diterbitkan</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#065f46', lineHeight: 1.2 }}>{totalDiterbitkan}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #fb6340' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Total THP</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#92400e', lineHeight: 1.2 }}>{fmtRupiah(totalTHP)}</div>
          </div>
        </div>
      )}

      {/* Toolbar: Generate | Filter Jenis | Search | Periode */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
        padding: '12px 16px', background: '#fff', borderRadius: 12,
        boxShadow: '0 0 2rem 0 rgba(136,152,170,0.1)',
        flexWrap: 'wrap',
      }}>
        <button className="btn btn-primary" onClick={openGenerate} style={{ whiteSpace: 'nowrap' }}>
          <Plus size={16} /> Generate Slip
        </button>
        <button
          className="btn"
          disabled={!filterJenis}
          title={filterJenis
            ? `Generate slip ${filterJenis.toLowerCase()} untuk semua karyawan (bagian yang dipilih)`
            : 'Pilih jenis Bulanan atau Mingguan dulu'}
          style={{
            background: 'var(--gray-100)', whiteSpace: 'nowrap',
            color: filterJenis ? 'var(--gray-600)' : 'var(--gray-300)',
            cursor: filterJenis ? 'pointer' : 'not-allowed',
          }}
          onClick={() => filterJenis && setShowBulk(isWeeklyFilter ? mondayOf(filterPeriode) : filterPeriode)}
        >
          Generate {filterJenis ? `${filterJenis} Massal` : 'Massal'}
        </button>
        <button
          className="btn"
          disabled={genLoading || filterJenis === 'Bulanan'}
          title={filterJenis === 'Bulanan'
            ? 'Tombol ini khusus penggajian Mingguan — ubah filter ke Mingguan atau Semua'
            : `Generate massal slip gaji mingguan untuk minggu berjalan (${labelRentangMinggu(thisWeekMonday())})${filterBagian ? ' — hanya bagian yang dipilih' : ''}`}
          style={{
            background: filterJenis === 'Bulanan' ? 'var(--gray-100)' : '#8965e0',
            color: filterJenis === 'Bulanan' ? 'var(--gray-300)' : '#fff',
            whiteSpace: 'nowrap',
            cursor: (genLoading || filterJenis === 'Bulanan') ? 'not-allowed' : 'pointer',
          }}
          onClick={handleGenerateThisWeek}
        >
          <CalendarDays size={15} /> {genLoading ? 'Memproses...' : 'Generate Minggu Ini'}
        </button>
        <button
          className="btn"
          disabled={!filterJenis || genLoading}
          title={filterJenis
            ? `Terbitkan semua slip ${filterJenis.toLowerCase()} yang masih Draft pada periode ini + buat jurnal akuntansi otomatis`
            : 'Pilih jenis Bulanan atau Mingguan dulu'}
          style={{
            background: filterJenis ? '#2dce89' : 'var(--gray-100)',
            borderColor: filterJenis ? '#2dce89' : 'var(--gray-100)',
            color: filterJenis ? '#fff' : 'var(--gray-300)',
            whiteSpace: 'nowrap',
            cursor: (!filterJenis || genLoading) ? 'not-allowed' : 'pointer',
          }}
          onClick={() => filterJenis && setShowPublishBulk(true)}
        >
          <CheckCircle size={15} /> {genLoading ? 'Memproses...' : 'Terbitkan Massal'}
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={filterBagian} onChange={e => setFilterBagian(e.target.value)}
            style={{ padding: '7px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.8rem', background: '#fff', color: 'var(--gray-600)' }}>
            <option value="">Semua Bagian</option>
            {bagianList.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[{ value: '', label: 'Semua' }, { value: 'Bulanan', label: 'Bulanan' }, { value: 'Mingguan', label: 'Mingguan' }].map(j => (
            <button
              key={j.value || 'all'}
              onClick={() => {
                setFilterJenis(j.value);
                setFilterPeriode(j.value === 'Mingguan' ? thisWeekMonday() : thisMonth());
              }}
              style={{
                padding: '7px 14px', borderRadius: 8, border: '1px solid var(--gray-200)',
                fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                background: filterJenis === j.value ? (j.value === 'Mingguan' ? '#8965e0' : (j.value === 'Bulanan' ? 'var(--cyan-500)' : 'var(--gray-600)')) : '#fff',
                color: filterJenis === j.value ? '#fff' : 'var(--gray-500)',
                transition: 'all 0.2s',
              }}
            >
              {j.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 200, maxWidth: 320 }}>
          <div className="search-box" style={{ width: '100%', margin: 0 }}>
            <Search size={16} />
            <input placeholder="Cari berdasarkan nama..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {isWeeklyFilter ? (
            <>
              <label style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontWeight: 500, whiteSpace: 'nowrap' }}>Minggu:</label>
              <button className="btn-icon" title="Minggu sebelumnya" style={{ padding: 4 }}
                onClick={() => setFilterPeriode(p => addDays(p || thisWeekMonday(), -7))}>
                <ChevronLeft size={16} />
              </button>
              <input
                type="date"
                value={filterPeriode}
                onChange={e => setFilterPeriode(e.target.value ? mondayOf(e.target.value) : '')}
                className="form-select"
                style={{ width: 140, padding: '7px 12px', borderRadius: 8, border: '1px solid var(--gray-300)', fontSize: '0.82rem' }}
              />
              <button className="btn-icon" title="Minggu berikutnya" style={{ padding: 4 }}
                onClick={() => setFilterPeriode(p => addDays(p || thisWeekMonday(), 7))}>
                <ChevronRight size={16} />
              </button>
              <span style={{ fontSize: '0.78rem', color: '#5b3fc4', fontWeight: 600, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <CalendarDays size={14} />{labelRentangMinggu(filterPeriode)}
              </span>
              {filterPeriode !== thisWeekMonday() && (
                <button style={btnPeriodeStyle} title="Kembali ke minggu berjalan"
                  onClick={() => setFilterPeriode(thisWeekMonday())}>Minggu Ini</button>
              )}
            </>
          ) : (
            <>
              <label style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontWeight: 500, whiteSpace: 'nowrap' }}>Periode:</label>
              <input
                type="month"
                value={filterPeriode}
                onChange={e => setFilterPeriode(e.target.value)}
                className="form-select"
                style={{ width: 140, padding: '7px 12px', borderRadius: 8, border: '1px solid var(--gray-300)', fontSize: '0.82rem' }}
              />
              {filterPeriode !== thisMonth() && (
                <button style={btnPeriodeStyle} title="Kembali ke bulan berjalan"
                  onClick={() => setFilterPeriode(thisMonth())}>Bulan Ini</button>
              )}
            </>
          )}
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>
            <strong style={{ color: 'var(--gray-600)' }}>{filtered.length}</strong>/{totalSemua}
          </span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="page-loading">
          {data.length > 0
            ? 'Tidak ada slip gaji yang cocok dengan filter (pencarian/bagian)'
            : isWeeklyFilter
              ? `Belum ada slip gaji untuk minggu ${labelRentangMinggu(filterPeriode)}`
              : 'Tidak ada data slip gaji untuk periode ini'}
        </div>
      ) : (
        <div className="regions-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))' }}>
          {filtered.map(d => {
            const mingguan = (d.jenis_gaji || 'Bulanan') === 'Mingguan';
            const kr = karyawanMap[d.karyawan_id] || {};
            return (
              <div key={d.id} className="region-card" style={{
                borderTop: `3px solid ${d.status === 'Diterbitkan' ? '#2dce89' : '#fb6340'}`
              }}>
                <div className="region-card-header" style={{
                  background: '#fff', borderBottom: '1px solid var(--gray-100)',
                  padding: '14px 16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: d.status === 'Diterbitkan'
                        ? 'linear-gradient(135deg, #2dce89, #1aae6f)'
                        : 'linear-gradient(135deg, #fb6340, #f5365c)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                    }}>
                      {d.status === 'Diterbitkan' ? <CheckCircle size={20} /> : <FileText size={20} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <h3 style={{ fontSize: '0.95rem', margin: 0, color: 'var(--gray-600)' }}>{d.nama}</h3>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 10px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 700,
                          background: mingguan ? '#ede9fe' : '#e0f2fe',
                          color: mingguan ? '#5b3fc4' : 'var(--cyan-700)',
                        }}>
                          {mingguan ? 'MINGGUAN' : 'BULANAN'}
                        </span>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 10px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 600,
                          background: d.status === 'Diterbitkan' ? '#d1fae5' : '#fef3c7',
                          color: d.status === 'Diterbitkan' ? '#065f46' : '#92400e',
                        }}>
                          <span style={{
                            width: 6, height: 6, borderRadius: '50%',
                            background: d.status === 'Diterbitkan' ? '#2dce89' : '#fb6340',
                          }} />
                          {d.status || 'Draft'}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>Periode: <strong style={{ color: 'var(--gray-500)' }}>{fmtPeriode(d)}</strong></span>
                        {kr.bagian_name && (
                          <span style={{
                            padding: '1px 8px', borderRadius: 20, fontSize: '0.65rem', fontWeight: 600,
                            background: 'var(--gray-100)', color: 'var(--gray-500)',
                          }}>
                            {kr.bagian_name}
                          </span>
                        )}
                        {d.status === 'Diterbitkan' && d.metode_pembayaran && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6,
                            padding: '1px 8px', borderRadius: 20, fontSize: '0.65rem', fontWeight: 600,
                            background: '#fce7f3', color: '#9d174d',
                          }}>
                            Metode: {d.metode_pembayaran}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="region-card-body" style={{ padding: '12px 16px' }}>
                  {mingguan ? (
                    <>
                      <div className="region-detail"><DollarSign size={14} className="text-muted" /><strong>Transport BBM:</strong> {fmtRupiah(d.tunjangan_transport_bbm)}</div>
                      <div className="region-detail"><DollarSign size={14} className="text-muted" /><strong>Sewa Kendaraan:</strong> {fmtRupiah(d.tunjangan_sewa_kendaraan)}</div>
                    </>
                  ) : (
                    <>
                      <div className="region-detail"><DollarSign size={14} className="text-muted" /><strong>Gaji Pokok:</strong> {fmtRupiah(d.gaji_pokok)}</div>
                      <div className="region-detail"><DollarSign size={14} className="text-muted" /><strong>Tunj. Jabatan:</strong> {fmtRupiah(d.tunjangan_jabatan)}</div>
                      <div className="region-detail"><DollarSign size={14} className="text-muted" /><strong>Insentif Keahlian (AC):</strong> {fmtRupiah(d.insentif_keahlian_ac)}</div>
                      <div className="region-detail"><DollarSign size={14} className="text-muted" /><strong>Insentif Project:</strong> {fmtRupiah(d.insentif_project)}</div>
                      <div className="region-detail" style={{ color: '#f5365c' }}><strong>Pot. Alpa:</strong> {fmtRupiah(d.potongan_alpa)}</div>
                      <div className="region-detail" style={{ color: '#f5365c' }}><strong>Pot. Kasbon:</strong> {fmtRupiah(d.potongan_kasbon)}</div>
                    </>
                  )}
                  {d.lembur > 0 && (
                    <div className="region-detail"><Clock size={14} className="text-muted" /><strong>Lembur:</strong> <span style={{ color: '#2dce89', fontWeight: 600 }}>{fmtRupiah(d.lembur)}</span></div>
                  )}
                  {d.keterangan && (
                    <div className="region-detail" style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 8, marginTop: 8, alignItems: 'flex-start' }}>
                      <FileText size={14} className="text-muted" />
                      <span><strong>Keterangan:</strong> {d.keterangan}</span>
                    </div>
                  )}
                  <div className="region-detail" style={{
                    borderTop: '2px solid var(--cyan-200)', paddingTop: 8, marginTop: 8,
                    background: mingguan ? '#f5f3ff' : 'var(--accent-tint-bg)', borderRadius: 8, padding: '10px 12px',
                  }}>
                    <strong style={{ color: mingguan ? '#5b3fc4' : 'var(--cyan-700)' }}>Take Home Pay:</strong>
                    <span style={{ fontWeight: 700, color: mingguan ? '#5b3fc4' : 'var(--cyan-700)', fontSize: '1rem' }}>{fmtRupiah(d.thp)}</span>
                  </div>
                </div>
                <div className="action-btns" style={{ padding: '10px 16px', borderTop: '1px solid var(--gray-100)', justifyContent: 'flex-end' }}>
                  <button className="btn-icon" title="Cetak Slip Gaji" onClick={() => generateSlipPDF(d)} style={{ color: 'var(--cyan-600)' }}>
                    <Printer size={16} />
                  </button>
                  {d.status !== 'Diterbitkan' && (
                    <button className="btn-icon" title="Terbitkan + Jurnal" onClick={() => openPublish(d)} style={{ color: '#2dce89' }}>
                      <CheckCircle size={16} />
                    </button>
                  )}
                  <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d.id)}><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showGenerate && (
        <div className="modal-overlay" onClick={() => setShowGenerate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, var(--cyan-500) 0%, var(--cyan-700) 100%)',
              margin: -1, padding: '20px 24px', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              display: 'flex', alignItems: 'center', gap: 12
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={18} color="white" />
              </div>
              <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>Generate Slip Gaji</h3>
            </div>
            <div className="modal-body">
              <div style={{
                background: 'var(--accent-tint-bg)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 16,
                border: '1px solid var(--cyan-100)'
              }}>
                <div className="form-group">
                  <label>Karyawan</label>
                  <select value={genForm.karyawan_id} onChange={e => onSelectKaryawan(parseInt(e.target.value))} required>
                    <option value="">Pilih Karyawan</option>
                    {karyawan.map(k => (
                      <option key={k.id} value={k.id}>
                        {k.nama} — {(gajiMap[k.id] || 'Bulanan')}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{genIsWeekly ? 'Periode Mingguan' : 'Periode Bulanan'}</label>
                  <input
                    type={genIsWeekly ? 'date' : 'month'}
                    value={genForm.periode}
                    onChange={e => setGenForm({ ...genForm, periode: genIsWeekly ? mondayOf(e.target.value) : e.target.value })}
                    required
                  />
                  {genIsWeekly && (
                    <small style={{ color: 'var(--gray-400)' }}>
                      Minggu penggajian: <strong style={{ color: '#5b3fc4' }}>{labelRentangMinggu(genForm.periode)}</strong>
                      {' '}— tanggal otomatis dibulatkan ke hari Senin.
                    </small>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowGenerate(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleGenerate} disabled={genLoading}>
                {genLoading ? <Loader size={16} className="spinner" /> : null} Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {showPublish && (
        <div className="modal-overlay" onClick={() => setShowPublish(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, #2dce89 0%, #1aae6f 100%)',
              margin: -1, padding: '20px 24px', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              display: 'flex', alignItems: 'center', gap: 12
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle size={18} color="white" />
              </div>
              <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>Terbitkan Slip Gaji</h3>
            </div>
            <div className="modal-body">
              <div style={{
                background: 'var(--accent-tint-bg)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 16,
                border: '1px solid var(--cyan-100)'
              }}>
                <p style={{ marginTop: 0 }}>
                  Terbitkan slip gaji <strong>{showPublish.nama}</strong> ({showPublish.jenis_gaji || 'Bulanan'}){' '}
                  periode <strong>{showPublish.periode}</strong>? Jurnal akuntansi akan dibuat otomatis:
                </p>
                <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 14, lineHeight: 1.7 }}>
                  <strong style={{ color: '#065f46' }}>Debit</strong> Beban Gaji (6100) — {fmtRupiah(showPublish.thp)}<br />
                  <strong style={{ color: '#9d174d' }}>Kredit</strong> {publishMetode === 'Kas' ? 'Kas (1110)' : publishMetode === 'Transfer' ? 'Bank (1101)' : 'Utang Gaji (2200)'} — {fmtRupiah(showPublish.thp)}
                </div>
                <div className="form-group">
                  <label>Metode Pembayaran</label>
                  <select value={publishMetode} onChange={e => setPublishMetode(e.target.value)}>
                    <option value="Hutang">Hutang Gaji (kredit Utang Gaji 2200)</option>
                    <option value="Kas">Kas (kredit Kas 1110)</option>
                    <option value="Transfer">Transfer Bank (kredit Bank 1101)</option>
                  </select>
                </div>
                <small style={{ color: 'var(--gray-400)' }}>
                  Pilih <strong>Hutang</strong> jika gaji dicatat sebagai utang dulu, atau <strong>Kas/Transfer</strong> jika langsung dibayarkan saat terbit.
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowPublish(null)}>Batal</button>
              <button className="btn" style={{ background: '#2dce89', borderColor: '#2dce89', color: '#fff' }} onClick={handlePublish} disabled={genLoading}>
                {genLoading ? <Loader size={16} className="spinner" /> : null} Terbitkan & Buat Jurnal
              </button>
            </div>
          </div>
        </div>
      )}

      {showPublishBulk && (
        <div className="modal-overlay" onClick={() => setShowPublishBulk(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, #2dce89 0%, #1a9e66 100%)',
              margin: -1, padding: '20px 24px', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              display: 'flex', alignItems: 'center', gap: 12
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle size={18} color="white" />
              </div>
              <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>Terbitkan Massal + Jurnal</h3>
            </div>
            <div className="modal-body">
              <div style={{
                background: '#f0fdf4', borderRadius: 'var(--radius)', padding: 16, marginBottom: 16,
                border: '1px solid #bbf7d0'
              }}>
                <p style={{ marginTop: 0 }}>
                  Terbitkan <strong>{draftPeriode.length} slip {filterJenis}</strong> yang masih <strong>Draft</strong>{' '}
                  {filterBagian ? `di bagian ${bagianList.find(b => String(b.id) === String(filterBagian))?.name || ''} ` : ''}
                  {isWeeklyFilter ? 'minggu ' : 'periode '}
                  <strong>{fmtPeriode({ periode: isWeeklyFilter ? mondayOf(filterPeriode) : filterPeriode, jenis_gaji: filterJenis })}</strong>?
                  Jurnal akuntansi dibuat otomatis per slip:
                </p>
                <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 14, lineHeight: 1.7 }}>
                  <strong style={{ color: '#065f46' }}>Debit</strong> Beban Gaji (6100) — {fmtRupiah(totalTHPDraft)}<br />
                  <strong style={{ color: '#9d174d' }}>Kredit</strong>{' '}
                  {publishBulkMetode === 'Kas' ? 'Kas (1110)' : publishBulkMetode === 'Transfer' ? 'Bank (1101)' : 'Utang Gaji (2200)'}
                  {' '}— {fmtRupiah(totalTHPDraft)}
                </div>
                <div className="form-group">
                  <label>Metode Pembayaran</label>
                  <select value={publishBulkMetode} onChange={e => setPublishBulkMetode(e.target.value)}>
                    <option value="Hutang">Hutang Gaji (kredit Utang Gaji 2200)</option>
                    <option value="Kas">Kas (kredit Kas 1110)</option>
                    <option value="Transfer">Transfer Bank (kredit Bank 1101)</option>
                  </select>
                </div>
                <small style={{ color: 'var(--gray-400)' }}>
                  {draftPeriode.length === 0
                    ? 'Tidak ada slip Draft pada periode ini — semuanya sudah diterbitkan.'
                    : 'Slip yang sudah diterbitkan sebelumnya dilewati, jadi jurnal tidak dibuat dobel.'}
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowPublishBulk(false)}>Batal</button>
              <button className="btn" style={{ background: '#2dce89', borderColor: '#2dce89', color: '#fff' }}
                onClick={handlePublishBulk} disabled={genLoading || draftPeriode.length === 0}>
                {genLoading ? <Loader size={16} className="spinner" /> : null} Terbitkan {draftPeriode.length} Slip & Buat Jurnal
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulk && (
        <div className="modal-overlay" onClick={() => setShowBulk(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, var(--cyan-500) 0%, var(--cyan-700) 100%)',
              margin: -1, padding: '20px 24px', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              display: 'flex', alignItems: 'center', gap: 12
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={18} color="white" />
              </div>
              <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>Generate Semua Slip Gaji</h3>
            </div>
            <div className="modal-body">
              <div style={{
                background: 'var(--accent-tint-bg)', borderRadius: 'var(--radius)', padding: 16,
                border: '1px solid var(--cyan-100)'
              }}>
                <p>
                  Generate slip gaji <strong>{filterJenis}</strong> untuk <strong>semua karyawan aktif</strong>{' '}
                  {filterBagian ? `di bagian ${bagianList.find(b => String(b.id) === String(filterBagian))?.name || ''} ` : ''}
                  {isWeeklyFilter ? 'minggu ' : 'periode '}
                  <strong>{fmtPeriode({ periode: showBulk, jenis_gaji: filterJenis })}</strong>?
                </p>
                <p style={{ fontSize: 13, color: 'var(--gray-400)' }}>
                  Slip gaji yang sudah ada pada periode ini akan diperbarui.
                  {isWeeklyFilter && ' Karyawan yang komponen gajinya Bulanan tidak ikut di-generate.'}
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowBulk(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleGenerateBulk} disabled={genLoading}>
                {genLoading ? <Loader size={16} className="spinner" /> : null} Generate Semua
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}